import Axios from "axios";
import {
  SET_LC_IMPORT_LC_AMENDMENT_LIST,
  UNSET_LC_IMPORT_LC_AMENDMENT_LIST,
  SET_EDITING_LC_IMPORT_LC_AMENDMENT,
  UNSET_EDITING_LC_IMPORT_LC_AMENDMENT,
} from "@/store/mutation-types";
import {
  saveUpdateLcImportLcAmendment,
  getLcImportLcAmendmentList,
  getLcImportLcAmendmentById as getLcImportLcAmendmentByIdApi,
} from "@/store/api";

// initial state
const state = {
  lcImportLcAmendmentList: [],
  editingLcImportLcAmendment: null,
};

const getters = {
  getLcImportLcAmendmentList(state) {
    return state.lcImportLcAmendmentList;
  },
  getEditingLcImportLcAmendment(state) {
    return state.editingLcImportLcAmendment;
  },
};

const mutations = {
  [SET_LC_IMPORT_LC_AMENDMENT_LIST](state, data) {
    state.lcImportLcAmendmentList = data;
  },
  [UNSET_LC_IMPORT_LC_AMENDMENT_LIST](state) {
    state.lcImportLcAmendmentList = [];
  },
  [SET_EDITING_LC_IMPORT_LC_AMENDMENT](state, data) {
    state.editingLcImportLcAmendment = data;
  },
  [UNSET_EDITING_LC_IMPORT_LC_AMENDMENT](state) {
    state.editingLcImportLcAmendment = null;
  },
};

const actions = {
  saveUpdateLcImportLcAmendment({ commit }, data) {
    commit("SET_LOADING", { color: "primary" });
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      
      // Append all form fields
      Object.keys(data).forEach(key => {
        if (key === 'documents' && Array.isArray(data[key])) {
          // Handle multiple file uploads
          data[key].forEach((file, index) => {
            formData.append(`documents[${index}]`, file);
          });
        } else if (key === 'amendment_types') {
          // Handle amendment_types array - send as FormData array notation
          if (Array.isArray(data[key]) && data[key].length > 0) {
            data[key].forEach((value, index) => {
              formData.append(`amendment_types[${index}]`, value);
            });
          }
          // Empty array - don't append (Laravel nullable|array will accept null/empty)
        } else if (key === 'updates') {
          // Handle updates - can be array or object (associative array)
          // Backend expects associative array (object) with key-value pairs
          if (Array.isArray(data[key])) {
            if (data[key].length > 0) {
              // If it's a non-empty array, send as array
              data[key].forEach((value, index) => {
                if (typeof value === 'object' && value !== null) {
                  Object.keys(value).forEach(subKey => {
                    formData.append(`updates[${index}][${subKey}]`, value[subKey]);
                  });
                } else {
                  formData.append(`updates[${index}]`, value);
                }
              });
            }
            // Empty array - don't append (Laravel nullable|array will accept null/empty)
          } else if (typeof data[key] === 'object' && data[key] !== null) {
            // If it's an object (associative array), send as key-value pairs
            const updatesObj = data[key];
            if (Object.keys(updatesObj).length > 0) {
              // Send as associative array using FormData notation
              // Laravel will receive this as an associative array (which passes array validation)
              Object.keys(updatesObj).forEach(subKey => {
                formData.append(`updates[${subKey}]`, updatesObj[subKey]);
              });
            }
            // Empty object - don't append (Laravel nullable|array will accept null/empty)
          }
        } else if (key === 'new_pis') {
          // Handle new_pis array - send as FormData array notation
          if (Array.isArray(data[key]) && data[key].length > 0) {
            data[key].forEach((piId, index) => {
              formData.append(`new_pis[${index}]`, piId);
            });
          }
          // Empty array - don't append (Laravel nullable|array will accept null/empty)
        } else if (key === 'pi_item_updates') {
          // Handle pi_item_updates array - send as FormData array notation
          if (Array.isArray(data[key])) {
            if (data[key].length > 0) {
              data[key].forEach((piUpdate, piIndex) => {
                formData.append(`pi_item_updates[${piIndex}][pi_id]`, piUpdate.pi_id || '');
                
                // Handle added array
                if (Array.isArray(piUpdate.added)) {
                  piUpdate.added.forEach((item, itemIndex) => {
                    formData.append(`pi_item_updates[${piIndex}][added][${itemIndex}][product_id]`, item.product_id || '');
                    formData.append(`pi_item_updates[${piIndex}][added][${itemIndex}][qty]`, item.qty || 0);
                    formData.append(`pi_item_updates[${piIndex}][added][${itemIndex}][rate]`, item.rate || 0);
                    formData.append(`pi_item_updates[${piIndex}][added][${itemIndex}][uom]`, item.uom || 'PCS');
                    if (item.hs_code) {
                      formData.append(`pi_item_updates[${piIndex}][added][${itemIndex}][hs_code]`, item.hs_code);
                    }
                  });
                }
                
                // Handle removed array
                if (Array.isArray(piUpdate.removed)) {
                  piUpdate.removed.forEach((item, itemIndex) => {
                    formData.append(`pi_item_updates[${piIndex}][removed][${itemIndex}][id]`, item.id || '');
                  });
                }
                
                // Handle updated array
                if (Array.isArray(piUpdate.updated)) {
                  piUpdate.updated.forEach((item, itemIndex) => {
                    formData.append(`pi_item_updates[${piIndex}][updated][${itemIndex}][id]`, item.id || '');
                    formData.append(`pi_item_updates[${piIndex}][updated][${itemIndex}][product_id]`, item.product_id || '');
                    formData.append(`pi_item_updates[${piIndex}][updated][${itemIndex}][qty]`, item.qty || 0);
                    formData.append(`pi_item_updates[${piIndex}][updated][${itemIndex}][rate]`, item.rate || 0);
                    formData.append(`pi_item_updates[${piIndex}][updated][${itemIndex}][uom]`, item.uom || 'PCS');
                    if (item.hs_code) {
                      formData.append(`pi_item_updates[${piIndex}][updated][${itemIndex}][hs_code]`, item.hs_code);
                    }
                  });
                }
              });
            }
            // Empty array - don't append (Laravel nullable|array will accept null/empty)
          }
        } else if (key !== 'documents') {
          if (typeof data[key] === 'object' && data[key] !== null) {
            formData.append(key, JSON.stringify(data[key]));
          } else {
            formData.append(key, data[key] || '');
          }
        }
      });

      Axios.post(saveUpdateLcImportLcAmendment, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
        .then(function (response) {
          resolve(response);
        })
        .catch(function (error) {
          reject(error);
        })
        .then((always) => {
          commit("UNSET_LOADING");
        });
    });
  },

  getLcImportLcAmendmentList({ commit }) {
    return new Promise((resolve, reject) => {
      Axios.get(getLcImportLcAmendmentList)
        .then(function (response) {
          commit(SET_LC_IMPORT_LC_AMENDMENT_LIST, response.data.data || []);
          resolve(response);
        })
        .catch(function (error) {
          commit(UNSET_LC_IMPORT_LC_AMENDMENT_LIST);
          reject(error);
        });
    });
  },

  getLcImportLcAmendmentById({ commit }, id) {
    const url = `${getLcImportLcAmendmentByIdApi}${id}`;
    commit("SET_LOADING", { color: "primary" });
    return new Promise((resolve, reject) => {
      Axios.get(url)
        .then((response) => {
          commit(SET_EDITING_LC_IMPORT_LC_AMENDMENT, response.data.data);
          resolve(response.data.data);
        })
        .catch((error) => {
          reject(error);
        })
        .then((always) => {
          commit("UNSET_LOADING");
        });
    });
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};

